import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Email Subscription
export const subscribeEmail = async (email, phone = null, source = 'popup') => {
  const response = await axios.post(`${API}/email/subscribe`, { email, phone, source });
  return response.data;
};

export const getEmailSubscribers = async () => {
  const response = await axios.get(`${API}/email/subscribers`);
  return response.data;
};

export const deleteEmailSubscriber = async (id) => {
  const response = await axios.delete(`${API}/email/subscribers/${id}`);
  return response.data;
};

// Coffee Pre-order
export const submitCoffeePreorder = async (email, phone = null) => {
  const response = await axios.post(`${API}/coffee-preorder`, { email, phone });
  return response.data;
};

export const getCoffeePreorders = async () => {
  const response = await axios.get(`${API}/coffee-preorders`);
  return response.data;
};

export const deleteCoffeePreorder = async (id) => {
  const response = await axios.delete(`${API}/coffee-preorders/${id}`);
  return response.data;
};

// Videos
export const getVideos = async (category = null, featured = null) => {
  const params = {};
  if (category) params.category = category;
  if (featured !== null) params.featured = featured;
  const response = await axios.get(`${API}/videos`, { params });
  return response.data;
};

export const getVideoCategories = async () => {
  const response = await axios.get(`${API}/videos/categories`);
  return response.data;
};

export const createVideo = async (videoData) => {
  const response = await axios.post(`${API}/videos`, videoData);
  return response.data;
};

export const updateVideo = async (videoId, videoData) => {
  const response = await axios.put(`${API}/videos/${videoId}`, videoData);
  return response.data;
};

export const deleteVideo = async (videoId) => {
  const response = await axios.delete(`${API}/videos/${videoId}`);
  return response.data;
};

export const reorderVideo = async (videoId, newOrder) => {
  const response = await axios.put(`${API}/videos/${videoId}/reorder?new_order=${newOrder}`);
  return response.data;
};

// Cloudflare Stream
export const getCloudflareUploadUrl = async (maxDurationSeconds = 3600) => {
  const response = await axios.post(`${API}/videos/cloudflare-upload-url`, {
    max_duration_seconds: maxDurationSeconds,
    creator_id: 'admin'
  });
  return response.data;
};

export const getCloudflareVideoDetails = async (videoUid) => {
  const response = await axios.get(`${API}/videos/cloudflare/${videoUid}`);
  return response.data;
};

// Proxy video upload through backend (works better on mobile)
export const proxyVideoUpload = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/videos/proxy-upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        onProgress(progress);
      }
    },
    timeout: 600000, // 10 minutes timeout for large files
  });
  return response.data;
};

// Import video from URL (Google Drive, Dropbox, etc.)
export const importVideoFromUrl = async (url, title = 'Imported Video') => {
  const response = await axios.post(`${API}/videos/import-from-url`, { url, title });
  return response.data;
};

// Images
export const getImages = async (category = null) => {
  const params = {};
  if (category) params.category = category;
  const response = await axios.get(`${API}/images`, { params });
  return response.data;
};

export const getImageCategories = async () => {
  const response = await axios.get(`${API}/images/categories`);
  return response.data;
};

export const createImage = async (imageData) => {
  const response = await axios.post(`${API}/images`, imageData);
  return response.data;
};

export const updateImage = async (imageId, imageData) => {
  const response = await axios.put(`${API}/images/${imageId}`, imageData);
  return response.data;
};

export const deleteImage = async (imageId) => {
  const response = await axios.delete(`${API}/images/${imageId}`);
  return response.data;
};

export const cropImage = async (imageId, cropData) => {
  const response = await axios.post(`${API}/images/${imageId}/crop`, cropData);
  return response.data;
};

export const getCloudflareImageUploadUrl = async () => {
  const response = await axios.post(`${API}/images/cloudflare-upload-url`);
  return response.data;
};

// Category Covers
export const updateCategoryCover = async (categoryKey, coverImage) => {
  const response = await axios.put(`${API}/videos/categories/${categoryKey}/cover`, { cover_image: coverImage });
  return response.data;
};

export const getCategoryCovers = async () => {
  const response = await axios.get(`${API}/videos/categories/covers`);
  return response.data;
};

// Coffee Products
export const getCoffeeProducts = async () => {
  const response = await axios.get(`${API}/coffee/products`);
  return response.data;
};

export const calculateShipping = async (shippingAddress, itemCount) => {
  const response = await axios.post(`${API}/coffee/calculate-shipping`, {
    ...shippingAddress,
    item_count: itemCount
  });
  return response.data;
};

export const createCoffeeOrder = async (orderData) => {
  const response = await axios.post(`${API}/coffee/orders`, orderData);
  return response.data;
};

export const getCoffeeOrder = async (orderId) => {
  const response = await axios.get(`${API}/coffee/orders/${orderId}`);
  return response.data;
};

// Donations
export const createDonation = async (donationData) => {
  const response = await axios.post(`${API}/donations`, donationData);
  return response.data;
};

export const getDonation = async (donationId) => {
  const response = await axios.get(`${API}/donations/${donationId}`);
  return response.data;
};

// Stripe Checkout
export const createDonationCheckout = async (donationId, amount, donationType) => {
  const originUrl = window.location.origin;
  const response = await axios.post(`${API}/checkout/donation?amount=${amount}`, {
    donation_id: donationId,
    donation_type: donationType,
    origin_url: originUrl,
    metadata: {
      source: 'web'
    }
  });
  return response.data;
};

export const createCoffeeCheckout = async (orderId) => {
  const originUrl = window.location.origin;
  const response = await axios.post(`${API}/checkout/coffee`, {
    order_id: orderId,
    origin_url: originUrl,
    metadata: {
      source: 'web'
    }
  });
  return response.data;
};

export const getCheckoutStatus = async (sessionId) => {
  const response = await axios.get(`${API}/checkout/status/${sessionId}`);
  return response.data;
};

// Statistics
export const getImpactStats = async () => {
  const response = await axios.get(`${API}/stats/impact`);
  return response.data;
};

// Analytics
export const getAnalyticsSummary = async (days = 30) => {
  const response = await axios.get(`${API}/analytics/summary`, { params: { days } });
  return response.data;
};

export const getPageAnalytics = async (days = 30) => {
  const response = await axios.get(`${API}/analytics/pages`, { params: { days } });
  return response.data;
};

export const getRecentVisitors = async (limit = 50) => {
  const response = await axios.get(`${API}/analytics/recent-visitors`, { params: { limit } });
  return response.data;
};

export default {
  subscribeEmail,
  getVideos,
  getVideoCategories,
  createVideo,
  updateVideo,
  deleteVideo,
  reorderVideo,
  getCloudflareUploadUrl,
  getCloudflareVideoDetails,
  proxyVideoUpload,
  getImages,
  getImageCategories,
  createImage,
  updateImage,
  deleteImage,
  getCloudflareImageUploadUrl,
  getCoffeeProducts,
  calculateShipping,
  createCoffeeOrder,
  getCoffeeOrder,
  createDonation,
  getDonation,
  createDonationCheckout,
  createCoffeeCheckout,
  getCheckoutStatus,
  getImpactStats
};